import {cast, flow, getSnapshot, Instance, types} from 'mobx-state-tree';
import {TRFEdgeDataConfig, TRFGraphDataConfig, TRFGraphZone} from '@progress-fe/rf-core';
import {Edge, Node} from '@xyflow/react';
import {BaseUpdateOut, GraphZone, RJSFSchemas, TechProcessApi} from 'api';
import {ResetModel, RequestModel, JsonSchemaForm} from 'core/models';
import {GRAPH_LIST} from 'core/mocks/projects/Separators/graph.mocks';
import {IEntityToRefresh} from 'core/interfaces';

const ProjectTask = types
  .compose(
    ResetModel,
    types.model('ProjectTask', {
      projectUuid: '',
      checkpointUuid: '',

      jsonForm: types.maybeNull(JsonSchemaForm),

      graphzoneLastUpdate: new Date(),
      nodes: types.optional(types.array(types.frozen<Node<TRFGraphDataConfig>>()), []),
      edges: types.optional(types.array(types.frozen<Edge<TRFEdgeDataConfig>>()), []),

      graphFetchRequest: types.optional(RequestModel, {}),
      taskFetchRequest: types.optional(RequestModel, {}),
      formUpdateRequest: types.optional(RequestModel, {})
    })
  )
  .actions((self) => ({
    _clearJsonForm() {
      self.jsonForm?.clear();
      self.jsonForm = null;
    },
    _reloadJsonForm: flow(function* () {
      self.jsonForm = JsonSchemaForm.create({entityUuid: ''});
      const response: {[key: string]: RJSFSchemas} = yield self.jsonForm.fetchRequest.send(
        TechProcessApi.techProcessGetTechprocessCalculationTasks.bind(TechProcessApi),
        {
          projectUuid: self.projectUuid,
          checkpointUuid: self.checkpointUuid
        }
      );

      if (response) {
        self.jsonForm = JsonSchemaForm.create({entityUuid: ''});
        self.jsonForm.setJsonSchemas(response);
      }
    })
  }))
  .actions((self) => ({
    _loadGraphZone: flow(function* () {
      const mockGraphZone = GRAPH_LIST.find((d) => d.projectId === self.projectUuid);
      if (mockGraphZone) {
        self.nodes = cast(mockGraphZone.nodes);
        self.edges = cast(mockGraphZone.edges);
        return;
      }

      const response: GraphZone | null = yield self.graphFetchRequest.send(
        TechProcessApi.techProcessGetCompGraph.bind(TechProcessApi),
        {
          projectUuid: self.projectUuid,
          checkpointUuid: self.checkpointUuid
        }
      );

      if (response) {
        self.nodes = cast((response as TRFGraphZone).nodes);
        self.edges = cast((response as TRFGraphZone).edges);
        self.graphzoneLastUpdate = new Date();
      } else {
        self.nodes = cast([]);
        self.edges = cast([]);
        self.graphzoneLastUpdate = new Date();
      }
    })
  }))
  .actions((self) => ({
    _updateTaskFormData: flow(function* (schemaId: string, data: unknown) {
      const jsonSchema = self.jsonForm?.jsonSchemas.find((js) => js.id === schemaId);
      if (!self.jsonForm || !jsonSchema) {
        return {changedEntities: null};
      }

      jsonSchema.updateFormData(data);
      const response: BaseUpdateOut = yield self.jsonForm.updateRequest.send(
        TechProcessApi.techProcessUpdateTechprocessCalculationTask.bind(TechProcessApi),
        {
          body: data as object,
          projectUuid: self.projectUuid,
          checkpointUuid: self.checkpointUuid
        }
      );

      if (response.schemas) {
        self.jsonForm?.setJsonSchemas(response.schemas);
      }

      const entityToRefresh: IEntityToRefresh = {
        dataChanged: response.dataChanged,
        modelsChanged: undefined,
        elementsChanged: undefined,
        logicalElementsChanged: undefined,
        treeChanged: response.treeChanged
      };

      return {
        changedEntities: entityToRefresh
      };
    })
  }))
  .actions((self) => ({
    _reload: flow(function* () {
      yield self._reloadJsonForm();
      yield self._loadGraphZone();
    })
  }))
  .actions((self) => ({
    init: flow(function* (projectUuid: string, checkpointUuid: string) {
      self.resetModel();
      self.projectUuid = projectUuid;
      self.checkpointUuid = checkpointUuid;
      yield self._reload();
    })
  }))
  .views((self) => ({
    get nodeList() {
      return getSnapshot(self.nodes);
    },
    get edgeList() {
      return getSnapshot(self.edges);
    }
  }))
  .views((self) => ({
    get isFormLoading(): boolean {
      return self.jsonForm?.isFormLoading ?? false;
    },
    get isFormDataUpdating(): boolean {
      return self.jsonForm?.isFormDataUpdating ?? false;
    }
  }));

export type TProjectTaskModel = Instance<typeof ProjectTask>;

export {ProjectTask};
